import { buildPathWithAdAccount } from "@/lib/adAccountIdUtils";
import { isSubmittedAdvertiserIdentityVerificationStatus } from "@/lib/advertiserIdentityVerificationStatus";
import { getUserHasSofaAccess } from "@/lib/getUserHasSofaAccess";
import {
  buildOnboardingCampaignSetupPath,
  ONBOARDING_CAMPAIGN_SETUP_PATH,
} from "@/lib/onboardingSetupFlow";
import { LOCALE_SEARCH_PARAM } from "@/locale";

import type { SofaAppLoadContext } from "express-server/main";

export const CREATE_NEW_AD_ACCOUNT_QUERY_PARAM = "create_new_ad_account";
export const PROSPECT_CAMPAIGN_TOKEN_QUERY_PARAM = "prospect_campaign_token";
export const PROSPECT_CAMPAIGN_TOKEN_STORAGE_KEY = "adsManagerProspectCampaignToken";
export const PROSPECT_ONBOARDING_TOKEN_HASH_PARAM = "prospect_onboarding_token";
export const PROSPECT_ONBOARDING_TOKEN_STORAGE_KEY = "adsManagerProspectOnboardingToken";
export const ADS_MANAGER_ACCOUNT_SELECTOR_CREATION_ENTRY_POINT =
  "ads_manager_account_selector" as const;
export const ADS_MANAGER_AVAILABILITY_HOLDING_SCREEN_CREATION_ENTRY_POINT =
  "ads_manager_availability_holding_screen" as const;
export type CreateNewAdAccountCreationEntryPoint =
  | typeof ADS_MANAGER_ACCOUNT_SELECTOR_CREATION_ENTRY_POINT
  | typeof ADS_MANAGER_AVAILABILITY_HOLDING_SCREEN_CREATION_ENTRY_POINT;

export function buildOnboardingPath({
  prefillId,
  oppref,
  prospectCampaignToken = null,
  locale = null,
}: {
  prefillId: string | null;
  oppref: string | null;
  prospectCampaignToken?: string | null;
  locale?: string | null;
}): string {
  if (prefillId == null && oppref == null && prospectCampaignToken == null && locale == null) {
    return "/onboarding";
  }

  const searchParams = new URLSearchParams();
  if (prefillId != null) {
    searchParams.set("prefill_id", prefillId);
  }
  if (oppref != null) {
    searchParams.set("oppref", oppref);
  }
  if (prospectCampaignToken != null) {
    searchParams.set(PROSPECT_CAMPAIGN_TOKEN_QUERY_PARAM, prospectCampaignToken);
  }
  if (locale != null) {
    searchParams.set(LOCALE_SEARCH_PARAM, locale);
  }
  return `/onboarding?${searchParams.toString()}`;
}

export function getProspectCampaignToken(searchParams: URLSearchParams): string | null {
  return normalizeProspectCampaignToken(searchParams.get(PROSPECT_CAMPAIGN_TOKEN_QUERY_PARAM));
}

export function getProspectCampaignTokenFromHash(hash: string): string | null {
  const fragment = hash.startsWith("#") ? hash.slice(1) : hash;
  return getProspectCampaignToken(new URLSearchParams(fragment));
}

export function hasProspectCampaignTokenInHash(hash: string): boolean {
  const fragment = hash.startsWith("#") ? hash.slice(1) : hash;
  return new URLSearchParams(fragment).has(PROSPECT_CAMPAIGN_TOKEN_QUERY_PARAM);
}

export function getStoredProspectCampaignToken(
  storage: ProspectCampaignTokenStorage | null = getSessionStorage(),
): string | null {
  if (!storage) {
    return null;
  }

  try {
    return normalizeProspectCampaignToken(storage.getItem(PROSPECT_CAMPAIGN_TOKEN_STORAGE_KEY));
  } catch {
    return null;
  }
}

export function storeProspectCampaignToken(
  token: string,
  storage: ProspectCampaignTokenStorage | null = getSessionStorage(),
): boolean {
  const normalizedToken = normalizeProspectCampaignToken(token);
  if (!storage || normalizedToken == null) {
    return false;
  }

  try {
    storage.setItem(PROSPECT_CAMPAIGN_TOKEN_STORAGE_KEY, normalizedToken);
    return true;
  } catch {
    return false;
  }
}

export function clearStoredProspectCampaignToken(
  storage: ProspectCampaignTokenStorage | null = getSessionStorage(),
): void {
  if (!storage) {
    return;
  }

  try {
    storage.removeItem(PROSPECT_CAMPAIGN_TOKEN_STORAGE_KEY);
  } catch {
    // Session storage may be unavailable in privacy-restricted browser contexts.
  }
}

export function getProspectOnboardingTokenFromHash(hash: string): string | null {
  const fragment = hash.startsWith("#") ? hash.slice(1) : hash;
  return normalizeProspectCampaignToken(
    new URLSearchParams(fragment).get(PROSPECT_ONBOARDING_TOKEN_HASH_PARAM),
  );
}

export function hasProspectOnboardingTokenInHash(hash: string): boolean {
  const fragment = hash.startsWith("#") ? hash.slice(1) : hash;
  return new URLSearchParams(fragment).has(PROSPECT_ONBOARDING_TOKEN_HASH_PARAM);
}

export function getStoredProspectOnboardingToken(
  storage: ProspectCampaignTokenStorage | null = getSessionStorage(),
): string | null {
  if (!storage) {
    return null;
  }
  try {
    return normalizeProspectCampaignToken(storage.getItem(PROSPECT_ONBOARDING_TOKEN_STORAGE_KEY));
  } catch {
    return null;
  }
}

export function storeProspectOnboardingToken(
  token: string,
  storage: ProspectCampaignTokenStorage | null = getSessionStorage(),
): boolean {
  const normalizedToken = normalizeProspectCampaignToken(token);
  if (!storage || normalizedToken == null) {
    return false;
  }
  try {
    storage.setItem(PROSPECT_ONBOARDING_TOKEN_STORAGE_KEY, normalizedToken);
    return true;
  } catch {
    return false;
  }
}

export function clearStoredProspectOnboardingToken(
  storage: ProspectCampaignTokenStorage | null = getSessionStorage(),
): void {
  if (!storage) {
    return;
  }
  try {
    storage.removeItem(PROSPECT_ONBOARDING_TOKEN_STORAGE_KEY);
  } catch {
    // Session storage may be unavailable in privacy-restricted browser contexts.
  }
}

export function buildPromotionOnboardingPath({
  adAccountId,
}: {
  adAccountId: string | null | undefined;
}): string {
  if (adAccountId) {
    return buildCreateNewAdAccountPath(adAccountId);
  }
  return "/onboarding";
}

export function buildPromotionLoginReturnPath(): string {
  return "/promotion";
}

export { buildOnboardingCampaignSetupPath, ONBOARDING_CAMPAIGN_SETUP_PATH };

export function buildCreateNewAdAccountPath(
  adAccountId: string,
  creationEntryPoint?: CreateNewAdAccountCreationEntryPoint,
): string {
  const url = new URL(buildPathWithAdAccount("/onboarding", adAccountId), "http://placeholder");
  url.searchParams.set(CREATE_NEW_AD_ACCOUNT_QUERY_PARAM, creationEntryPoint ?? "1");
  return `${url.pathname}?${url.searchParams.toString()}`;
}

export function hasCreateNewAdAccountIntent(searchParams: URLSearchParams): boolean {
  const createNewAdAccountIntent = searchParams.get(CREATE_NEW_AD_ACCOUNT_QUERY_PARAM);
  return (
    createNewAdAccountIntent === "1" ||
    createNewAdAccountIntent === ADS_MANAGER_ACCOUNT_SELECTOR_CREATION_ENTRY_POINT ||
    createNewAdAccountIntent === ADS_MANAGER_AVAILABILITY_HOLDING_SCREEN_CREATION_ENTRY_POINT
  );
}

export function getCreateNewAdAccountCreationEntryPoint(
  searchParams: URLSearchParams,
): CreateNewAdAccountCreationEntryPoint | undefined {
  const creationEntryPoint = searchParams.get(CREATE_NEW_AD_ACCOUNT_QUERY_PARAM);
  if (
    creationEntryPoint === ADS_MANAGER_ACCOUNT_SELECTOR_CREATION_ENTRY_POINT ||
    creationEntryPoint === ADS_MANAGER_AVAILABILITY_HOLDING_SCREEN_CREATION_ENTRY_POINT
  ) {
    return creationEntryPoint;
  }
  return undefined;
}

export function getCreateNewAdAccountSourceId({
  currentAdAccountId,
  hasCreateNewAdAccountIntent,
  isPendingInvitationsRoute,
}: {
  currentAdAccountId?: string;
  hasCreateNewAdAccountIntent: boolean;
  isPendingInvitationsRoute: boolean;
}): string | undefined {
  if (!hasCreateNewAdAccountIntent && !isPendingInvitationsRoute) {
    return undefined;
  }

  return currentAdAccountId;
}

export function canStartCreateNewAdAccountFromPendingInvitations({
  canStartCreateFlow,
  isCreateNewAdAccountEnabled,
}: {
  canStartCreateFlow: boolean;
  isCreateNewAdAccountEnabled?: boolean;
}): boolean {
  return canStartCreateFlow && isCreateNewAdAccountEnabled === true;
}

export function getCreateNewAdAccountSourceHeaders(
  sourceAdAccountId?: string,
): Record<string, string> {
  return sourceAdAccountId ? { "OpenAI-Ad-Account": sourceAdAccountId } : {};
}

export function getUnavailableRedirectPath(context: SofaAppLoadContext): string | null {
  if (!context.user) {
    return "/auth/login";
  }

  if (!getUserHasSofaAccess(context)) {
    return context.isSelfServeOnboardEnabled ? "/onboarding" : null;
  }

  if (shouldShowVerificationKickoff(context) || shouldShowPostVerificationHoldingScreen(context)) {
    return buildPathWithAdAccount("/onboarding", context.adAccountId);
  }

  return buildPathWithAdAccount("/manage/campaigns", context.adAccountId);
}

export function shouldShowVerificationKickoff(context: SofaAppLoadContext): boolean {
  return (
    context.adAccountId != null &&
    getAccountOnboardingAvailabilityHoldReason(context) == null &&
    context.adAccountNeedsPersonaVerification === true &&
    !isSubmittedAdvertiserIdentityVerificationStatus(context.adAccountPersonaVerificationStatus)
  );
}

export function shouldShowPostVerificationHoldingScreen(context: SofaAppLoadContext): boolean {
  if (context.adAccountId != null && getAccountOnboardingAvailabilityHoldReason(context) != null) {
    return true;
  }

  return (
    context.adAccountId != null &&
    context.isSelfServeOnboardEnabled === true &&
    context.adAccountShouldBeOnHoldingScreenPostVerification === true &&
    isSubmittedAdvertiserIdentityVerificationStatus(context.adAccountPersonaVerificationStatus)
  );
}

function getAccountOnboardingAvailabilityHoldReason(context: SofaAppLoadContext): string | null {
  return (
    context.adAccountOnboardingAvailabilityHoldReason ??
    context.adAccountAdsManagerAccessHoldReason ??
    null
  );
}

type ProspectCampaignTokenStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

function normalizeProspectCampaignToken(token: string | null): string | null {
  const normalizedToken = token?.trim();
  return normalizedToken && normalizedToken.length >= 32 && normalizedToken.length <= 4096 ?
      normalizedToken
    : null;
}

function getSessionStorage(): Storage | null {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}
